import { appConfig } from "@/shared/config/env";

export type ApiFieldErrors = Record<string, string[]>;

export type ApiMeta = {
  request_id?: string;
  pagination?: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
};

export type ApiEnvelope<T> = {
  data: T;
  meta?: ApiMeta;
};

type ErrorPayload = {
  message?: string;
  code?: string;
  errors?: ApiFieldErrors;
  request_id?: string;
};

export type ApiRequestOptions = RequestInit & {
  timeoutMs?: number;
  suppressUnauthorizedEvent?: boolean;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly errors: ApiFieldErrors;
  readonly requestId?: string;

  constructor(
    status: number,
    message: string,
    code = "API_ERROR",
    errors: ApiFieldErrors = {},
    requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.errors = errors;
    this.requestId = requestId;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function fieldErrors(value: unknown): ApiFieldErrors {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value).flatMap(([field, messages]) => {
      if (!Array.isArray(messages)) return [];
      const strings = messages.filter(
        (message): message is string => typeof message === "string",
      );
      return strings.length > 0 ? [[field, strings]] : [];
    }),
  );
}

function errorPayload(value: unknown): ErrorPayload {
  if (!isRecord(value)) return {};
  return {
    message: typeof value.message === "string" ? value.message : undefined,
    code: typeof value.code === "string" ? value.code : undefined,
    errors: fieldErrors(value.errors),
    request_id:
      typeof value.request_id === "string" ? value.request_id : undefined,
  };
}

function cookie(name: string) {
  const encoded = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=");
  return encoded ? decodeURIComponent(encoded) : undefined;
}

let csrfPromise: Promise<void> | null = null;

async function ensureCsrf(signal?: AbortSignal) {
  if (!csrfPromise) {
    const apiOrigin = new URL(appConfig.apiBaseUrl).origin;
    csrfPromise = fetch(`${apiOrigin}/sanctum/csrf-cookie`, {
      credentials: "include",
      headers: { Accept: "application/json" },
      signal,
    })
      .then((response) => {
        if (!response.ok && response.status !== 204) {
          throw new ApiError(
            response.status,
            "Unable to initialize the secure session.",
            "CSRF_INITIALIZATION_FAILED",
          );
        }
      })
      .finally(() => {
        csrfPromise = null;
      });
  }
  await csrfPromise;
}

export function apiUrl(path: string) {
  if (/^https?:\/\//i.test(path)) {
    const candidate = new URL(path);
    if (candidate.origin !== new URL(appConfig.apiBaseUrl).origin) {
      throw new ApiError(0, "The requested API URL is not trusted.", "INVALID_API_URL");
    }
    return candidate.toString();
  }
  return `${appConfig.apiBaseUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

export async function apiEnvelope<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  const {
    timeoutMs = appConfig.apiTimeoutMs,
    suppressUnauthorizedEvent = false,
    ...requestOptions
  } = options;
  const method = (requestOptions.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    await ensureCsrf(requestOptions.signal ?? undefined);
  }

  const controller = new AbortController();
  const forwardAbort = () => controller.abort(requestOptions.signal?.reason);
  requestOptions.signal?.addEventListener("abort", forwardAbort, { once: true });
  const timeout = window.setTimeout(
    () => controller.abort(new DOMException("Request timed out", "TimeoutError")),
    timeoutMs,
  );

  const headers = new Headers(requestOptions.headers);
  headers.set("Accept", "application/json");
  headers.set("X-Request-ID", crypto.randomUUID());
  if (requestOptions.body && !(requestOptions.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const xsrfToken = cookie("XSRF-TOKEN");
  if (xsrfToken) headers.set("X-XSRF-TOKEN", xsrfToken);

  try {
    const response = await fetch(apiUrl(path), {
      ...requestOptions,
      method,
      headers,
      credentials: "include",
      signal: controller.signal,
    });
    const contentType = response.headers.get("content-type") ?? "";
    const payload: unknown = contentType.includes("application/json")
      ? await response.json().catch(() => undefined)
      : undefined;

    if (!response.ok) {
      const error = errorPayload(payload);
      if (response.status === 401 && !suppressUnauthorizedEvent) {
        window.dispatchEvent(new CustomEvent("tawzeef-admin:unauthorized"));
      }
      if (response.status === 403 && !suppressUnauthorizedEvent) {
        window.dispatchEvent(new CustomEvent("tawzeef-admin:session-check"));
      }
      throw new ApiError(
        response.status,
        error.message ?? "The Tawzeef API request failed.",
        error.code,
        error.errors,
        error.request_id ?? response.headers.get("X-Request-ID") ?? undefined,
      );
    }

    if (!isRecord(payload) || !("data" in payload)) {
      throw new ApiError(
        0,
        "The Tawzeef API returned an invalid response.",
        "INVALID_API_RESPONSE",
        {},
        response.headers.get("X-Request-ID") ?? undefined,
      );
    }

    return payload as ApiEnvelope<T>;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (controller.signal.aborted) {
      throw new ApiError(0, "The request timed out.", "REQUEST_TIMEOUT");
    }
    throw new ApiError(0, "Unable to reach the Tawzeef API.", "NETWORK_ERROR");
  } finally {
    window.clearTimeout(timeout);
    requestOptions.signal?.removeEventListener("abort", forwardAbort);
  }
}

export async function apiClient<T>(
  path: string,
  options: ApiRequestOptions = {},
) {
  return (await apiEnvelope<T>(path, options)).data;
}

export function resetCsrfState() {
  csrfPromise = null;
}

/** Credentialed image reads; never return an HTML error page as an image. */
export async function apiImage(path: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(apiUrl(path), {
    credentials: "include",
    headers: { Accept: "application/json, image/png, image/jpeg, image/webp", "X-Request-ID": crypto.randomUUID() },
    signal: AbortSignal.any([...(signal ? [signal] : []), AbortSignal.timeout(appConfig.apiTimeoutMs)]),
    cache: "no-store",
  });
  if (response.status === 401) window.dispatchEvent(new CustomEvent("tawzeef-admin:unauthorized"));
  if (response.status === 403) window.dispatchEvent(new CustomEvent("tawzeef-admin:session-check"));
  if (!response.ok) throw new ApiError(response.status, "The image is not available.");
  if (!/^image\/(png|jpeg|webp)(;|$)/i.test(response.headers.get("content-type") ?? "")) {
    throw new ApiError(0, "The API returned an invalid image.", "INVALID_API_RESPONSE");
  }
  return response.blob();
}

/** Case-authorized private file streams; reject HTML/login pages. */
export async function apiFile(path: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(apiUrl(path), { credentials: "include", headers: { Accept: "application/pdf, image/jpeg, image/png, image/webp, application/octet-stream, application/json", "X-Request-ID": crypto.randomUUID() }, signal: AbortSignal.any([...(signal ? [signal] : []), AbortSignal.timeout(appConfig.apiTimeoutMs)]), cache: "no-store" });
  if (response.status === 401) window.dispatchEvent(new CustomEvent("tawzeef-admin:unauthorized"));
  if (response.status === 403) window.dispatchEvent(new CustomEvent("tawzeef-admin:session-check"));
  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    const error = errorPayload(payload);
    throw new ApiError(response.status, error.message ?? "The attachment is not available.", error.code);
  }
  if (!/^(application\/(pdf|octet-stream)|image\/(jpeg|png|webp))(;|$)/i.test(response.headers.get("content-type") ?? "")) throw new ApiError(0, "The API returned an invalid file.", "INVALID_API_RESPONSE");
  return response.blob();
}

/** Owner-authorized aggregate CSV only; reject HTML/login responses. */
export async function apiCsv(path: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(apiUrl(path), { credentials: "include", headers: { Accept: "text/csv, application/json", "X-Request-ID": crypto.randomUUID() }, signal: AbortSignal.any([...(signal ? [signal] : []), AbortSignal.timeout(appConfig.apiTimeoutMs)]), cache: "no-store" });
  if (response.status === 401) window.dispatchEvent(new CustomEvent("tawzeef-admin:unauthorized"));
  if (response.status === 403) window.dispatchEvent(new CustomEvent("tawzeef-admin:session-check"));
  if (!response.ok) {
    const error = errorPayload(await response.json().catch(() => undefined));
    throw new ApiError(response.status, error.message ?? "Export download is unavailable.", error.code);
  }
  if (!/^text\/csv(;|$)/i.test(response.headers.get("content-type") ?? "")) throw new ApiError(0, "The API returned an invalid CSV.", "INVALID_API_RESPONSE");
  return response.blob();
}
